import React, { useState, useRef, useEffect, forwardRef } from "react";
import { ReactSVG } from "react-svg";
import { setSVGColor } from "../SVGHelper";
import { useFormContext } from "react-hook-form";
import useOptionallyControlled from "../hooks/useOptionallyControlled";

import "../style/colors.scss";
import "../style/typography.scss";
import "../style/inputs.scss";
import "../style/buttons.scss";

import infoIcon from "../icon/Info.svg";
import maskedIcon from "../icon/EyeSlash.svg";
import unMaskedIcon from "../icon/Eye.svg";
import lockIcon from "../icon/Lock.svg";
import downArrow from "../icon/dashboard/Arrow/IOS/Down.svg";
import BrOnNewlineChar from "./BrOnNewlineChar";

/**
 * Input component using styles from inputs.scss and optionally controlled by react-hook-form.
 * Optional help text, custom icon, dropdowns, and edit/save button, secure input, left/right icon positioning.
 *
 * note: this is a block level element by default but you can change it inline by passing in 'display' value to style prop
 *
 * @param {*} param0
 * @returns
 */
const Input = (
    {
        label, // label text next to input
        name, // name of the input for forms stuff
        reactDatepickerName, //! hacky way to make react-datepicker work with our input component when using react-hook-form, for some reason, react-datepicker always sets name to undefined
        isDatePicker = false, //! hacky way to force making this component recognize itself as a datepicker when reactDatePickerName isn't set (it won't be set if it's used outside of react-hook-form)
        value: controlledValue,
        formOptions,
        disabled: disabledProp,
        forceEnableLabel = false, // when true the label will not be greyed out if the input is disabled
        helpText, // optional help text below input
        svg = undefined, // optional svg icon
        iconAfter = false, // if true, icon will be after input instead of before
        filterFunc = (value) => value, // return filtered value
        secure = false, // hide the input, (with button to show)
        editSaveBtn = false, // show edit/save button
        prefix,
        onChange,
        noShrinkBottom = false, // prevent this from collapsing when help is empty
        noShrinkTop = false, // prevent this from collapsing when label is empty
        noShrink, // convenience prop for noShrinkTop + noShrinkBottom
        dropdownOptions, // optional dropdown options, if only 1 option, will just show it without dropdown button
        selectedDropdownOption,
        onSelectFromDropdown,
        style = {},
        containerClassName = "",
        inputClassName = "input-bordered",
        onSave = () => {}, // callback for save button, only relevant if editSaveBtn is true
        ...restProps // any other props to pass to the underlying input
    },
    ref,
) => {
    const inherentInputPadding = 4;
    const iconSize = 20;
    const sideAreaItemPadding = 4;

    if (noShrink) {
        noShrinkTop = true;
        noShrinkBottom = true;
    }

    const [paddingLeft, setPaddingLeft] = useState(0);
    const [paddingRight, setPaddingRight] = useState(0);
    const leftSideRef = useRef();
    const rightSideRef = useRef();
    useEffect(() => {
        setPaddingLeft(leftSideRef?.current.offsetWidth + inherentInputPadding);
        setPaddingRight(rightSideRef?.current.offsetWidth + inherentInputPadding);
    }, [
        leftSideRef?.current?.offsetWidth,
        rightSideRef?.current?.offsetWidth,
        dropdownOptions,
        selectedDropdownOption,
    ]);

    const [showingHelp, setShowingHelp] = useState(false);
    const [maskOverride, setMaskOverride] = useState(false); // temporary override to show otherwise masked input
    const [disabled, setDisabled] = useState(
        disabledProp === undefined ? editSaveBtn : disabledProp, //if disabled is explicitly set, use it, otherwise initially disabled if editSaveBtn is true
    );

    const formContext = useFormContext();

    // react-datepicker needs special handling. Specifically, it needs to be in controlled mode instead of uncontrolled
    // when used with react-hook-form (usually, components need to be uncontrolled when used with react-hook-form).
    const isDatepickerInput = isDatePicker || !!reactDatepickerName;

    const inputElementName = reactDatepickerName || name;

    const [propsForInput] = useOptionallyControlled(
        inputElementName,
        controlledValue,
        onChange,
        formOptions,
        filterFunc,
        isDatepickerInput,
    );

    const inputId = `input-${inputElementName}`;

    const error = formContext?.formState?.errors?.[inputElementName]?.message;
    if (error && showingHelp) setShowingHelp(false); // only show help if no error is being shown

    const infoOrError = error || (showingHelp && helpText ? helpText : undefined);

    const rightSideIcons = [];
    const leftSideIcons = [];

    if (helpText) {
        rightSideIcons.push({
            svg: infoIcon,
            onClick: () => {
                if (!error) setShowingHelp(!showingHelp);
            }, // only show help if no error is being shown
        });
    }

    if (svg) {
        const iconData = { svg: svg, onClick: () => {} };
        if (iconAfter) {
            rightSideIcons.push(iconData);
        } else {
            leftSideIcons.unshift(iconData);
        }
    }

    if (secure) {
        rightSideIcons.push({
            svg: maskOverride ? unMaskedIcon : maskedIcon,
            onClick: () => setMaskOverride(!maskOverride),
        });
        leftSideIcons.push({
            svg: lockIcon,
        });
    }

    const hasStuffForLeftSide = !!(prefix || dropdownOptions || leftSideIcons?.length > 0);

    //todo: extract code to component or custom hook and and share with proper Dropdown component
    const DropdownOptions = () => {
        return (
            <>
                {dropdownOptions && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: dropdownOptions.length > 1 ? "pointer" : "default",
                            paddingLeft: `${sideAreaItemPadding}px`,
                            paddingRight: `${sideAreaItemPadding}px`,
                            pointerEvents: dropdownOptions.length > 1 ? "auto" : "none",
                        }}
                        onClick={() => console.debug("todo: actually open a menu")} // todo: setDropdownOpen(!isDropdownOpen)}
                    >
                        <p
                            className={`paragraph-5-semibold`}
                            style={{
                                color: "var(--state-color-icon)",
                                marginLeft: "auto",
                                marginRight: "-2px",
                            }}
                        >
                            {selectedDropdownOption || dropdownOptions[0]}
                        </p>
                        <ReactSVG
                            src={downArrow}
                            beforeInjection={(svg) => {
                                setSVGColor("var(--state-color-icon)", false, ["white"])(svg);
                            }}
                            style={{
                                width: `${iconSize}px`,
                            }}
                        />
                    </div>
                )}
                {/*todo: {isDropdownOpen && (
          <div style={{
            position: 'absolute',
            zIndex: 999999, // so it renders on top of other elements
            border: '1px solid var(--state-color-border)',
            backgroundColor: 'white',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', // adding a shadow for better distinction
          }}>
            {dropdownOptions.map((option, index) => (
              <div 
                key={index}
                style={{
                  padding: '10px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  onSelectFromDropdown(option);
                  setDropdownOpen(false);
                }}
              >
                {option}
              </div>
            ))}
          </div>
        )} */}
            </>
        );
    };

    // displays the left side and right side icons
    const Icons = ({ icons }) => {
        return (
            <>
                {icons.map((iconData, i) => (
                    <ReactSVG
                        key={i}
                        src={iconData.svg}
                        beforeInjection={(svg) => {
                            svg.setAttribute("width", `${iconSize}px`);
                            svg.setAttribute("height", `${iconSize}px`);
                            setSVGColor("var(--state-color-icon)", false, ["white"])(svg);
                        }}
                        onClick={(e) => {
                            iconData.onClick(e);
                            document.getElementById(inputId)?.focus(); // focus the input when an icon is clicked todo: this doesn't work...
                        }}
                        style={{
                            width: `${iconSize}px`,
                            marginLeft: `${sideAreaItemPadding}px`,
                            marginRight: `${sideAreaItemPadding}px`,
                            marginTop: "auto",
                            marginBottom: "auto",
                            pointerEvents: iconData.onClick ? "pointer" : "none", //todo: get the pointer icon going here
                        }}
                    />
                ))}
            </>
        );
    };

    //defining styling for the nested areas that position themselves "in" the input field
    const InlineSideAreasStyle = {
        position: "absolute",
        height: "100%",
        display: "flex",
        paddingLeft: `${sideAreaItemPadding}px`,
        paddingRight: `${sideAreaItemPadding + hasStuffForLeftSide * inherentInputPadding}px`,
        marginLeft: `${inherentInputPadding}px`,
        marginRight: `${inherentInputPadding}px`,
    };
    const LeftInlineSideAreaStyle = {
        ...InlineSideAreasStyle,
    };
    const RightInlineSideAreaStyle = {
        ...InlineSideAreasStyle,
        right: "0%",
    };

    return (
        <div
            className={`
        input-container
        ${forceEnableLabel ? "label-force-enabled" : ""}
        ${error ? "error" : ""}
        ${disabled ? "disabled" : ""}
        ${containerClassName}
      `}
            style={style}
        >
            {/* TOP LABEL */}
            <label
                className={`input-label`}
                style={{
                    visibility: label ? "visible" : "hidden", // hide label if no label, needed if noShrink puts blank space here
                }}
            >
                {label || (noShrinkTop ? "\u00A0" : "")}
            </label>

            {/* INPUT AND INLINE EXTRAS */}
            <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
                <div style={{ display: "inline-flex", flexDirection: "row", position: "relative", width: "100%" }}>
                    <div ref={leftSideRef} style={LeftInlineSideAreaStyle}>
                        <Icons icons={leftSideIcons} />
                        <DropdownOptions />
                        <p
                            className={`paragraph-5-semibold`}
                            style={{
                                display: prefix ? "inline" : "none",
                                margin: "auto",
                                color: "var(--state-color-icon)",
                                paddingLeft: `${sideAreaItemPadding}px`,
                                paddingRight: `${sideAreaItemPadding}px`,
                            }}
                        >
                            {prefix}
                        </p>
                        {hasStuffForLeftSide && (
                            <div
                                style={{
                                    borderLeft: "1px solid var(--state-color-border)",
                                    height: "16px",
                                    position: "absolute",
                                    right: `${inherentInputPadding}px`,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                }}
                            />
                        )}
                    </div>
                    <input
                        ref={ref}
                        type={secure && !maskOverride ? "password" : undefined}
                        id={inputId}
                        disabled={disabled}
                        {...propsForInput}
                        style={{ paddingRight: paddingRight, paddingLeft: paddingLeft, minWidth: "0px" }}
                        onBlur={(e) => setMaskOverride(false)}
                        {...restProps}
                        //* Intentionally putting className after rest props to prevent it from being overridden.
                        //* Adding the className from restProps to the input element so that third-party libraries like react-datepicker can add their own classes to the input element.
                        // More details: react-datepicker needs to add the "react-datepicker-ignore-onclickoutside" class when the input is clicked. If it's not added, you won't be able
                        // to click inside the input after the calendar is opened. Clicking when the calendar is open allows moving the caret (text cursor) inside the input.
                        className={`${inputClassName} ${restProps.className || ""}`}
                    />
                    <div ref={rightSideRef} style={RightInlineSideAreaStyle}>
                        <Icons icons={rightSideIcons} />
                    </div>
                </div>
                {editSaveBtn && (
                    <button
                        style={{
                            width: 50,
                            marginLeft: "16px",
                            alignSelf: "center",
                            display: "inline-flex",
                            justifyContent: "center",
                        }}
                        className="btn-primary btn-xs ignore-disabled-ancestors"
                        onClick={() => {
                            if (disabled) {
                                setDisabled(false);
                            } else {
                                setDisabled(true);
                                onSave();
                            }
                        }}
                    >
                        {disabled ? "Edit" : "Save"}
                    </button>
                )}
            </div>

            {/* BOTTOM INFO/ERROR TEXT */}
            <span
                className="input-info-text"
                style={{
                    visibility: infoOrError ? "visible" : "hidden", // hide if no text here, needed if noShrink puts blank space here
                }}
            >
                {infoOrError ? <BrOnNewlineChar text={infoOrError} /> : noShrinkBottom ? "\u00A0" : ""}
            </span>
        </div>
    );
};

export default forwardRef(Input);
